import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { StampChip } from '@/components/StampChip';
import { SPACE, T } from '@/design/tokens';
import { friendlyAuthError } from '@/features/auth/authErrors';
import { useAuth } from '@/features/auth/useAuth';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

interface LocationState {
  from?: { pathname?: string };
}

export function SignIn() {
  const { user, loading, signIn } = useAuth();
  const location = useLocation();
  const isOnline = useOnlineStatus();
  const [pending, setPending] = useState<'google' | 'apple' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/vehicles';

  if (loading) return null;
  if (user) return <Navigate to={redirectTo} replace />;

  const handle = (provider: 'google' | 'apple') => async () => {
    setError(null);
    setPending(provider);
    try {
      await signIn(provider);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setPending(null);
    }
  };

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: T.bg,
        padding: SPACE.lg,
      }}
    >
      <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
        <StampChip size="lg" style={{ margin: '0 auto' }} />
        <Heading level={1} size="xl" style={{ marginTop: SPACE.lg }}>
          Sign in to your garage
        </Heading>

        <div
          style={{
            marginTop: SPACE['2xl'],
            display: 'flex',
            flexDirection: 'column',
            gap: SPACE.md,
          }}
        >
          <Button
            variant="primary"
            size="lg"
            fullWidth
            loading={pending === 'google'}
            disabled={pending !== null || !isOnline}
            onClick={handle('google')}
          >
            Continue with Google
          </Button>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            loading={pending === 'apple'}
            disabled={pending !== null || !isOnline}
            onClick={handle('apple')}
          >
            Continue with Apple
          </Button>
        </div>

        {!isOnline ? (
          <Card accent="orange" style={{ marginTop: SPACE.md, textAlign: 'left' }}>
            <div role="alert" style={{ display: 'flex', gap: SPACE.xs, alignItems: 'flex-start' }}>
              <AlertTriangle
                size={14}
                aria-hidden="true"
                style={{ color: T.orange, flexShrink: 0, marginTop: 2 }}
              />
              <p style={{ margin: 0, fontSize: 13, color: T.text, lineHeight: 1.5 }}>
                You're offline. Sign-in needs a connection — try again once you're back online.
              </p>
            </div>
          </Card>
        ) : null}

        {error ? (
          <p
            role="alert"
            style={{
              marginTop: SPACE.md,
              color: T.red,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            {error}
          </p>
        ) : null}
      </div>
    </main>
  );
}
